<script lang="ts">
	/**
	 * Base Menu Context
	 *
	 * Provides all menu contexts globally for the entire app:
	 * - Memory menu context (for transcripts/notes)
	 * - Task menu context (future)
	 * - Workspace menu context (future)
	 * - Shared workspace data
	 * - Icon preloading for native menus
	 *
	 * This component wraps the entire app to ensure all menu contexts
	 * are always available for any item type.
	 */
	import type { WorkspaceInfo } from '@repo/common/types/ContextMenu';
	import { workspaceStore } from '@repo/sharedlib/src/stores/workspace.store';
	import constants from '@repo/sharedlib/src/utils/constants';
	import { setContext } from 'svelte';
	// Rename modal state (centralized)
	import RenameModal from '@repo/ui/src/components/ui/rename-modal/RenameModal.svelte';

	interface Props {
		children?: import('svelte').Snippet<[]>;
	}

	let { children }: Props = $props();

	// Shared workspace data derived from workspaceStore
	// This is computed once and shared by all menu contexts
	const workspacesData = $derived(
		Array.from($workspaceStore.workspaces.values()).map((ws) => ({
			id: ws.id,
			name: ws.name,
			color: ws.data?.color ?? undefined,
			isSelected: false // This will be set per-menu based on selected items
		}))
	);

	// Provide workspace data to all child components
	setContext('sharedMenuWorkspaces', {
		getWorkspaces: (): WorkspaceInfo[] => workspacesData
	});

	let renameModalOpen = $state(false);
	let renameModalItemId = $state('');
	let renameModalItemType = $state<'memory' | 'draft' | 'thread' | 'start'>('memory');
	let renameModalPosition = $state({ x: 0, y: 0 });
	let iconPreloadInitialized = $state(false);

	function openRenameModal(
		itemId: string,
		itemType: 'memory' | 'draft' | 'thread' | 'start',
		modalPosition: { x: number; y: number }
	) {
		// Try to find the title element for smart positioning
		// We assume a standard ID convention: `${itemType}-title-${itemId}`
		// but fallback to the provided position if not found.
		const elementId = `${itemType}-title-${itemId}`;
		const titleElement = document.getElementById(elementId);

		let finalPosition = modalPosition;

		if (titleElement) {
			const rect = titleElement.getBoundingClientRect();
			// Adjust for modal padding (p-2 = 8px) to align text
			finalPosition = {
				x: rect.left - 12,
				y: rect.top - 10
			};
		}

		console.log('[BaseMenuContext] Opening rename modal', {
			itemId,
			itemType,
			source: titleElement ? 'element' : 'mouse',
			position: finalPosition
		});

		renameModalItemId = itemId;
		renameModalItemType = itemType;
		renameModalPosition = finalPosition;
		renameModalOpen = true;
	}

	// Provide base menu actions to all child components
	setContext('baseMenuActions', {
		openRenameModal
	});

	$effect(() => {
		if (constants.PLATFORM === 'desktop' || constants.PLATFORM === 'macos') {
			if (!iconPreloadInitialized) {
				iconPreloadInitialized = true;

				// Dynamic import to avoid loading native code on web
				import('@repo/nativelib/src/menus/menuIconPreloader')
					.then(({ initializeMenuIconPreloading }) => {
						return initializeMenuIconPreloading();
					})
					.catch((error) => {
						console.error('[BaseMenuContext] Failed to initialize icon preloading:', error);
					});
			}
		}
	});
</script>

{@render children?.()}

<RenameModal
	bind:open={renameModalOpen}
	itemId={renameModalItemId}
	itemType={renameModalItemType}
	x={renameModalPosition.x}
	y={renameModalPosition.y}
/>
